import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ChatChannel, ChatMessage } from '../../shared/api/types';
import { Alert, Button, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { timeAgo } from '../site/PublicChurchSite';
import { useChatSocket } from './useChatSocket';
import { Avatar } from '../../shared/ui/Avatar';

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
const dayOf = (iso: string) => new Date(iso).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' });

function Conversation({ churchId, channel, onRead }: { churchId: string; channel: ChatChannel; onRead: () => void }) {
  const me = useMe();
  const base = `/communication/churches/${churchId}/chats/${channel.id}`;
  const [messages, setMessages] = useState<ChatMessage[] | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);

  const add = (m: ChatMessage) => setMessages((prev) => (prev && !prev.some((x) => x.id === m.id) ? [...prev, m] : prev));
  const status = useChatSocket(churchId, channel.id, (m) => {
    add(m);
    void api.post(`${base}/read`).catch(() => undefined);
  });

  useEffect(() => {
    let cancelled = false;
    setMessages(null);
    setLoadError(null);
    api
      .get<{ items: ChatMessage[] }>(`${base}/messages`)
      .then((r) => {
        if (cancelled) return;
        setMessages(r.items);
        void api.post(`${base}/read`).then(onRead, () => undefined);
      })
      .catch((e: unknown) => !cancelled && setLoadError(e));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: 'end' });
  }, [messages?.length]);

  async function send(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    const body = text.trim();
    if (!body) return;
    setSending(true);
    setError(null);
    try {
      add(await api.post<ChatMessage>(`${base}/messages`, { body }));
      setText('');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  }

  let lastDay = '';
  return (
    <section className="chat" aria-label={`Chat de ${channel.name}`}>
      <header className="chat__header">
        <Link to="/app/chats" className="chat__back">
          ← Chats
        </Link>
        <h2>{channel.name}</h2>
        <span className={`chat__status chat__status--${status}`}>{status === 'open' ? 'Conectado' : status === 'connecting' ? 'Conectando…' : 'Reconectando…'}</span>
      </header>
      <div className="chat__messages" role="log" aria-live="polite">
        {loadError ? <ErrorState error={loadError} /> : null}
        {!messages && !loadError ? <Loading /> : null}
        {messages && messages.length === 0 ? <p className="muted center">Todavía no hay mensajes. ¡Escribe el primero!</p> : null}
        {messages?.map((m) => {
          const day = dayOf(m.createdAt);
          const showDay = day !== lastDay;
          lastDay = day;
          const mine = m.author.personId === me.personId;
          return (
            <div key={m.id}>
              {showDay ? <p className="chat__day">{day}</p> : null}
              <div className={`chat__bubble ${mine ? 'chat__bubble--mine' : ''}`}>
                {!mine ? (
                  <Link to={`/app/personas/${m.author.personId}`} className="chat__author avatar-row">
                    <Avatar personId={m.author.personId} name={m.author.name} size={22} />
                    {m.author.name}
                  </Link>
                ) : null}
                <span className="chat__text">{m.body}</span>
                <span className="chat__time">{timeOf(m.createdAt)}</span>
              </div>
            </div>
          );
        })}
        <div ref={bottom} />
      </div>
      {error ? <Alert>{error}</Alert> : null}
      <form className="chat__composer" onSubmit={send}>
        <label htmlFor="chat-input" className="visually-hidden">
          Mensaje para {channel.name}
        </label>
        <textarea id="chat-input" rows={1} maxLength={4000} placeholder="Escribe un mensaje…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} />
        <Button type="submit" busy={sending} disabled={!text.trim()}>
          Enviar
        </Button>
      </form>
    </section>
  );
}

/** Chats internos de los ministerios en los que participo. */
export function ChatsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const { channelId } = useParams();
  // Canales leídos en esta visita: su contador se muestra en cero sin recargar la lista.
  const [readNow, setReadNow] = useState<Set<string>>(() => new Set());
  const channels = useAsync(
    (signal) => (churchId ? api.get<{ items: ChatChannel[] }>(`/communication/churches/${churchId}/chats`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId],
  );
  if (!churchId) {
    return (
      <div className="page">
        <h1>Chats</h1>
        <EmptyState title="Elige primero una iglesia en el selector de arriba." />
      </div>
    );
  }
  const open = channels.status === 'ready' ? channels.data.find((c) => c.id === channelId) : undefined;
  const unreadOf = (c: ChatChannel) => (readNow.has(c.id) ? 0 : c.unread);

  // Al abrir uno, la conversación completa ocupa la pantalla (con «← Chats» para volver).
  if (channelId) {
    return (
      <div className="page chats-open">
        {channels.status === 'loading' ? <Loading /> : null}
        {channels.status === 'error' ? <ErrorState error={channels.error} onRetry={channels.reload} /> : null}
        {channels.status === 'ready' && !open ? <EmptyState title="Ese chat ya no está disponible." /> : null}
        {open ? <Conversation key={open.id} churchId={churchId} channel={open} onRead={() => setReadNow((prev) => new Set(prev).add(open.id))} /> : null}
      </div>
    );
  }

  // Los que tienen mensajes sin leer arriba; luego por actividad más reciente.
  const sorted = channels.status === 'ready' ? [...channels.data].sort((a, b) => Number(unreadOf(b) > 0) - Number(unreadOf(a) > 0) || (b.lastMessage ? Date.parse(b.lastMessage.at) : 0) - (a.lastMessage ? Date.parse(a.lastMessage.at) : 0)) : [];
  return (
    <div className="page">
      <header className="page__header">
        <h1>Chats</h1>
        <p className="muted small">Un chat por cada ministerio y célula en la que participas.</p>
      </header>
      {channels.status === 'loading' ? <Loading /> : null}
      {channels.status === 'error' ? <ErrorState error={channels.error} onRetry={channels.reload} /> : null}
      {channels.status === 'ready' && channels.data.length === 0 ? (
        <EmptyState title="Todavía no participas en ningún ministerio.">
          <Link to="/app/vocacion">Busca dónde servir</Link>
        </EmptyState>
      ) : null}
      <ul className="chat-cards">
        {sorted.map((c) => (
          <li key={c.id}>
            <Link to={`/app/chats/${c.id}`} className={`chat-card${unreadOf(c) > 0 ? ' chat-card--unread' : ''}`}>
              <span className="chat-card__icon" aria-hidden="true">
                {c.kind === 'celula' ? '🏠' : '👥'}
              </span>
              <span className="chat-card__main">
                <span className="chat-card__top">
                  <strong className="chat-card__name">{c.name}</strong>
                  {c.lastMessage ? <span className="chat-card__when">{timeAgo(c.lastMessage.at)}</span> : null}
                </span>
                <span className="chat-card__last">
                  {c.lastMessage ? (
                    <>
                      {c.lastMessage.authorName ? <strong>{c.lastMessage.authorName}: </strong> : null}
                      {c.lastMessage.body}
                    </>
                  ) : (
                    <span className="muted">Sin mensajes todavía</span>
                  )}
                </span>
              </span>
              {unreadOf(c) > 0 ? (
                <span className="chat-row__badge" aria-label={`${unreadOf(c)} sin leer`}>
                  {unreadOf(c)}
                </span>
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
